/**
 * Inspired by the Material Design Input:
 * https://github.com/angular/components/blob/master/src/material/input/input.ts
 */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, ElementRef, HostBinding, HostListener, inject, Input } from '@angular/core';


@Directive({
  selector: `
  input[muiInputBase],
  select[muiInputBase],
  textarea[muiInputBase]`,
  exportAs: 'muiInputBase',
  standalone: true
})
export class MuiInputNativeDirective {

  protected static _nextUniqueId = 0;

  get valid(): boolean {
    return this._elementRef.nativeElement.checkValidity();
  }

  get invalid(): boolean {
    return !this.valid;
  }

  @Input()
  @HostBinding('attr.id')
  get id(): string {
    return this._id ?? this._uid;
  }

  set id(value: string) {
    this._id = value;
  }

  @Input()
  @HostBinding('required')
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }

  @HostBinding('class.has--content')
  get hasContent(): boolean {
    return !!this._elementRef.nativeElement.value;
  }

  @HostBinding('class.has--value')
  get hasValue(): boolean {
    return this.hasContent;
  }

  @HostBinding('class.has--placeholder')
  get hasPlaceholder(): boolean {
    return !!this.placeholder;
  }

  @HostBinding('class.has--error')
  get hasError(): boolean {
    return this.invalid;
  }

  @HostBinding('attr.aria-describedby')
  get describedBy(): string {
    return this.id + '-hint';
  }

  get isFocused(): boolean {
    return this._elementRef.nativeElement === document.activeElement;
  }

  @Input()
  @HostBinding('attr.placeholder')
  placeholder: string | null = null;

  @HostBinding('class.mui-input__control')
  protected _baseClass = true;

  protected _uid = `mui-input-${MuiInputNativeDirective._nextUniqueId++}`;
  protected _id: string | null = null;
  protected _required = false;
  protected _elementRef: ElementRef<HTMLInputElement> = inject(ElementRef, { self: true });
  protected _htmlInputValue = '';

  @HostListener('keydown.escape', ['$event'])
  resetToInitialValue(event: Event): void {
    event.preventDefault();
    this.blur();
  }

  blur(): void {
    this._elementRef.nativeElement.blur();
  }

  clear(): void {
    this._elementRef.nativeElement.value = '';
  }

  @HostListener('input', ['$event'])
  private _onHtmlInputEvent(event: KeyboardEvent): void {
    /* This triggers the change detection in order to update the CSS classes in time. */
    this._htmlInputValue = (event.target as HTMLInputElement).value;
  }
}
